import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Button from '../../../atoms/buttons/Button';
import { s3BucketContentDir } from '../../../config/Master';
import VideoGear from '../../../icons/VIdeoGear';
import MediaPlayIcon from '../../../atoms/icons/MediaPlayIcon';
import useMixPanel from '../../../hooks/useMixPanel';
import useNav from '../../../hooks/useNav';
import { useDisableBodyElementScroll } from '../../../hooks/useDisableBodyScroll';
import { Profile, ProfileMaker } from '../../../models/Profile';
import { Account, AccountMaker } from '../../../models/Account';
import FavOfferCreate from '../Offers/FavOfferCreate';
import { counterOffer, createNewOffer, sendSuggestion } from '../../../actions/offerActions';
import OfferSuccess from '../Offers/OfferSuccess';
import { displayWarnMessage } from '../../../actions/notificationActions';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import {
  mixpanelTrackLightBoxClosed,
  updateMixpanelLightBoxClosedPayload,
} from '../../../utils/mixpanel/lightboxClosed';
import Lightbox from '../../common/Lightbox';
import './PhotoLightbox.css';
import { IAccount } from '../../../types/common';
import { AppDispatch, RootState } from '../../../store';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';
import breakpoints from 'theme/material/breakpoints';

const PrivatePhotoMessageContainer = styled('div')({
  position: 'fixed',
  zIndex: 1001,
  color: '#ffffff',
  textAlign: 'center',
  left: 0,
  bottom: '12%',
  width: '100%',
});

const VideoContainer = styled('div')({
  position: 'fixed',
  zIndex: 1002,
  color: '#ffffff',
  textAlign: 'center',
  left: '50%',
  top: 'calc(50% + 30px)',
  transform: 'translate(-50%, -50%)',
  maxWidth: 480,
  width: '100%',
  display: 'flex',
  alignContent: 'center',
  alignItems: 'center',
  [breakpoints.down(639.05)]: {
    maxWidth: 'none',
  },
});

const VideoAction = styled('div')({
  zIndex: 1001,
  height: '100%',
  width: '100%',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const VideoNavButton = styled('button')({
  border: 'none',
  position: 'absolute',
  top: '-60px',
  bottom: 0,
  width: '20px',
  height: '34px',
  padding: '40px 30px',
  margin: 'auto',
  cursor: 'pointer',
  opacity: 0.7,
  zIndex: 1003,
  ':hover': {
    opacity: 1,
  },
  display: 'none',
  [breakpoints.down(479.05)]: {
    display: 'block',
  },
});

const MediaNavButtonRight = styled(VideoNavButton)({
  right: 0,
  background:
    'rgba(0, 0, 0, 0.2) url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIgd2lkdGg9IjIwIiBoZWlnaHQ9IjM0Ij48cGF0aCBkPSJtIDEsMyAyLC0yIDE2LDE2IC0xNiwxNiAtMSwtMSAxNSwtMTUgLTE1LC0xNSB6IiBmaWxsPSIjRkZGIi8+PC9zdmc+") no-repeat center',
});

const MediaNavButtonLeft = styled(VideoNavButton)({
  left: 0,
  background:
    'rgba(0, 0, 0, 0.2) url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIgd2lkdGg9IjIwIiBoZWlnaHQ9IjM0Ij48cGF0aCBkPSJtIDE5LDMgLTIsLTIgLTE2LDE2IDE2LDE2IDEsLTEgLTE1LC0xNSAxNSwtMTUgeiIgZmlsbD0iI0ZGRiIvPjwvc3ZnPg==) no-repeat center',
});

const VideoPlayer = styled('video')({
  margin: '0 auto',
  width: '95%',
  zIndex: 1000,
  [breakpoints.down(834.05)]: {
    maxWidth: 'none',
    width: '90%',
  },
});

const setUseStateOptions = (currentUserIsGenerous, type) => {
  switch (type) {
    case 'button':
      return currentUserIsGenerous ? 'Make Offer' : 'Request Offer';
    case 'private':
      return currentUserIsGenerous ? 'an offer' : 'a request';
    default:
      return;
  }
};

const renderPrivatePhotoLabel = (userHasPrivatePhotos, photoIndex, photoListLength, setShowPrivatePhotoLabel) => {
  if (userHasPrivatePhotos) {
    setShowPrivatePhotoLabel(photoIndex === parseInt(photoListLength, 10) - 1);
  }
};

const privateLabelCond = (lightboxIsOpen, userHasPrivatePhotos, currentImageProps, photoListLength) => {
  return lightboxIsOpen && userHasPrivatePhotos && currentImageProps === parseInt(photoListLength, 10) - 1;
};

const setCurrImgProps = currentImageProps => currentImageProps || 0;

const manyOffers = (cannot_make_offer_reasons, showWarn, handleOffersLimitError) => {
  const cond = cannot_make_offer_reasons[0].indexOf('many unlocked offers') > -1;

  if (cond) handleOffersLimitError();
};

const renderOfferOptions = (cannot_make_offer_reasons, showWarn, onOfferClick, handleOffersLimitError) => {
  if (!cannot_make_offer_reasons) {
    onOfferClick(false);
  } else if (cannot_make_offer_reasons[0]) {
    manyOffers(cannot_make_offer_reasons, showWarn, handleOffersLimitError);
  } else {
    showWarn(`You cannot make an offer: ${cannot_make_offer_reasons[0]}`);
  }
};

const srcImage = (images, cond) => {
  return images.length > 1 ? images[cond].src : undefined;
};

interface PhotoLightboxProps {
  images: any;
  allowZoom: any;
  lightboxIsOpen: any;
  privatePhotoCount: any;
  isOnboarded: any;
  offer: any;
  justSuggested: any;
  ignoreStatus: any;
  isOfferBtnVisible: any;
  currentImage: any;
  userHasPrivatePhotos: any;
  onClose: any;
  photoListLength: any;
  myprofile: any;
  onOfferClick: any;
  showWarn: any;
  currentUserIsGenerous?: any;
  account: IAccount;
  avatar: any;
  isPhotoMessage: boolean;
  ownAccount: Account;
  ownProfile: Profile;
  createNewOffer: any;
  sendSuggestion: any;
  counterOffer: any;
}

const PhotoLightbox = ({
  images,
  allowZoom,
  lightboxIsOpen,
  privatePhotoCount = 0,
  isOnboarded = true,
  offer,
  justSuggested,
  ignoreStatus,
  isOfferBtnVisible = false,
  currentImage: currentImageProps,
  userHasPrivatePhotos,
  onClose,
  photoListLength,
  myprofile,
  onOfferClick,
  account,
  avatar,
  isPhotoMessage = false,
  ownAccount,
  ownProfile,
  createNewOffer,
  sendSuggestion,
  showWarn,
  counterOffer,
}: PhotoLightboxProps): any => {
  const [currentImage, setCurrentImage] = useState(setCurrImgProps(currentImageProps));
  const [showPrivatePhotoLabel, setShowPrivatePhotoLabel] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [hasJustCompleteMandatoryProfile, setHasJustCompleteMandatoryProfile] = useState(false);
  const videoRef = useRef();
  const { trackPackagesPageVisited, mixpanelData, resetMixpanelData, setMixpanelDataByKeyHandler } = useMixPanel();
  const { goTo } = useNav();
  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();

  const { showMandatoryWall } = useMandatoryWall();

  const [videoPoster, setVideoPoster] = useState('');
  const [photoViewCount, setPhotoViewCount] = useState(1);
  const [showOfferSuccessModal, setShowOfferSuccessModal] = useState({
    isOpen: false,
    amount: '',
    isCounter: false,
  });
  const [showFavOfferCreateModal, setShowFavOfferCreateModal] = useState(false);
  const buttonText = useMemo(() => setUseStateOptions(ownProfile.isGenerous(), 'button'), [ownProfile.isGenerous()]);
  const privateText = useMemo(() => setUseStateOptions(ownProfile.isGenerous(), 'private'), [ownProfile.isGenerous()]);

  useDisableBodyElementScroll(lightboxIsOpen);

  /**
   * Need to handle in useEffect
   * because for some reason the `myprofile.cannot_make_offer_reasons`
   * is not being updated when the user finish and submit the mandatory profile modal
   */
  useEffect(() => {
    if (hasJustCompleteMandatoryProfile) {
      renderOfferOptions(myprofile?.cannot_make_offer_reasons, showWarn, onOfferClick, handleOffersLimitError);
    }
  }, [hasJustCompleteMandatoryProfile, myprofile?.cannot_make_offer_reasons]);

  useEffect(() => {
    renderVideoPlayer(currentImage);

    if (typeof images[currentImage]?.src !== 'undefined') {
      const thumbnail = images[currentImage].src;
      const shouldAppendHashInThumbnail = ['pending', 'new'].includes(images[currentImage]?.meta?.status);
      const mediaUrl = new URL(thumbnail);
      const hasSignedUrl = mediaUrl.searchParams.has('Signature');

      setVideoPoster(
        shouldAppendHashInThumbnail && !hasSignedUrl ? `${thumbnail}?hash=${new Date().getTime()}` : thumbnail
      );
    }
  }, [currentImageProps, currentImage, images.length]);

  useEffect(() => {
    if (privateLabelCond(lightboxIsOpen, userHasPrivatePhotos, currentImageProps, photoListLength)) {
      setShowPrivatePhotoLabel(true);
    }
  }, [lightboxIsOpen, userHasPrivatePhotos, currentImageProps, photoListLength]);

  useEffect(() => {
    setCurrentImage(currentImageProps);
    setVideoSrc(null);
    renderVideoPlayer(currentImageProps);
  }, [currentImageProps]);

  useEffect(() => {
    if (videoSrc) setShowPlayButton(true);
  }, [videoSrc]);

  const getTitle = (curr, len, isPrivateVisible = false) => {
    if (isPhotoMessage) return;

    const privateEye = `${s3BucketContentDir}thumbs/private_eye.png`;

    return (
      <span>
        {' '}
        {`${curr} of ${len} ${isPrivateVisible ? '- Private' : ''}`}
        {isPrivateVisible && <img src={privateEye} style={{ width: 20 + 'px', marginLeft: 5 + 'px' }} alt="" />}
      </span>
    );
  };

  useEffect(() => {
    if (photoViewCount > 0) {
      updateMixpanelLightBoxClosedPayload(setMixpanelDataByKeyHandler, photoViewCount);
    }
  }, [photoViewCount]);

  const closeLightbox = () => {
    onClose(currentImage);
    setCurrentImage(-1);
    setShowPrivatePhotoLabel(false);

    if (ownProfile?.hashId() !== account?.hash_id) {
      mixpanelTrackLightBoxClosed(mixpanelData, resetMixpanelData);
      setPhotoViewCount(1);
    }
  };

  const gotoPrevious = () => {
    const photoIndex = (currentImage + images.length - 1) % images.length;

    setCurrentImage(photoIndex);
    setVideoSrc(null);
    renderVideoPlayer(photoIndex);
    renderPrivatePhotoLabel(userHasPrivatePhotos, photoIndex, photoListLength, setShowPrivatePhotoLabel);
    setPhotoViewCount(count => count + 1);
  };

  const gotoNext = () => {
    const photoIndex = (currentImage + 1) % images.length;

    setCurrentImage(photoIndex);
    setVideoSrc(null);
    renderPrivatePhotoLabel(userHasPrivatePhotos, photoIndex, photoListLength, setShowPrivatePhotoLabel);
    renderVideoPlayer(photoIndex);
    setPhotoViewCount(count => count + 1);
  };

  const renderVideoPlayer = useCallback(
    (mediaIndex: number) => {
      if (images) {
        const media = images[mediaIndex];
        const isVideo = media && media.type === 'video';

        setVideoSrc(null);
        setShowVideoPlayer(false);

        if (isVideo) {
          setShowVideoPlayer(true);
          setTimeout(() => {
            setVideoSrc(images[mediaIndex].meta.mediaUrl);
          }, 50);
        }
      }
    },
    [images]
  );

  const handleOffersLimitError = () => {
    trackPackagesPageVisited({ Source: 'Offers Limit' });
    goTo('/packages?newPayWall=true');
  };

  const handleOfferSuccess = (toggle: boolean, amount = '', isCounter = false) => {
    resetMixpanelData(EVENT_NAMES.OFFER_MADE);

    setShowOfferSuccessModal({
      isOpen: toggle,
      amount,
      isCounter,
    });
  };

  const handleCreateNewOffer = formData => {
    const mxOfferMadePayload = mixpanelData[EVENT_NAMES.OFFER_MADE];

    return createNewOffer({ ...formData, source: mxOfferMadePayload.source });
  };

  const handleSuggestion = profileId => {
    return sendSuggestion(profileId);
  };

  const handleCounterOffer = formData => {
    return counterOffer(formData);
  };

  const handleOfferClick = () => {
    // open offer modal
    const { cannot_make_offer_reasons } = myprofile;

    showMandatoryWall(
      () => {
        const shouldShowOldMandatoryProfileModal =
          ownProfile.isGenerous() && cannot_make_offer_reasons && cannot_make_offer_reasons[0];

        if (shouldShowOldMandatoryProfileModal) {
          if (ownAccount.shouldShowTooManyLockedOffersDeals()) {
            setShowTooManyPendingOfferModal(true);
            return;
          } else if (ownAccount.shouldShowTooManySentOffers()) {
            setShowTooManyPendingOfferModal(true);
            setModalArgs({
              type: 'sent',
            });
            return;
          }
        }

        setHasJustCompleteMandatoryProfile(false);

        setShowFavOfferCreateModal(true);
      },
      () => undefined,
      'Make Offer'
    );
  };

  if (!lightboxIsOpen || !images[currentImage] || !images[currentImage].src) {
    return null;
  }

  const renderVideoProcessing = () => {
    if (showPrivatePhotoLabel) {
      return null;
    }

    return (
      showVideoPlayer && (
        <VideoContainer>
          <div style={{ width: '100%', flex: 1 }}>
            <VideoGear />
            <div
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              style={{ fontWieght: 'bold', marginTop: 10 }}
            >
              Video Processing...
            </div>
          </div>
        </VideoContainer>
      )
    );
  };

  const onVideoPlay = () => {
    if (videoSrc && videoRef && videoRef.current) {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      videoRef.current.load();
      setTimeout(() => {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        videoRef.current
          .play()
          .then(() => {
            setShowPlayButton(false);
          })
          .catch(error => {
            console.error('The play() request was interrupted:', error);
          });
      }, 0);
    }
  };

  const renderNewFeatureMandatoryPopup = () => {
    return (
      <Button
        type="button"
        buttonType="primaryRed"
        onClick={handleOfferClick}
        disabled={offer || justSuggested || ignoreStatus}
      >
        {buttonText}
      </Button>
    );
  };

  // To keep, will e useful later when experiment is enable
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderControlProfileCompletion = () => {
    return isOnboarded ? (
      <Button
        type="button"
        buttonType="primaryRed"
        onClick={handleOfferClick}
        disabled={offer || justSuggested || ignoreStatus}
      >
        {buttonText}
      </Button>
    ) : (
      <React.Fragment>
        <Button type="button" buttonType="primaryRed" onClick={handleOfferClick}>
          {buttonText}
        </Button>
      </React.Fragment>
    );
  };

  const mainSrc = images[currentImage].type === 'video' && !showPrivatePhotoLabel ? '' : images[currentImage].src;

  return (
    <React.Fragment>
      <Lightbox
        animationDisabled
        mainSrc={mainSrc}
        nextSrc={srcImage(images, (currentImage + 1) % images.length)}
        prevSrc={srcImage(images, (currentImage + images.length - 1) % images.length)}
        onCloseRequest={closeLightbox}
        onMovePrevRequest={gotoPrevious}
        onMoveNextRequest={gotoNext}
        clickOutsideToClose={true}
        enableZoom={allowZoom && images[currentImage].type !== 'video'}
        imageTitle={getTitle(
          currentImage + 1,
          images.length,
          images[currentImage].private === false && images[currentImage].privateReal === true
        )}
        wrapperClassName="profile-lightbox"
        currentImageIndex={currentImage}
      />
      {!showPrivatePhotoLabel && showVideoPlayer && videoSrc && images[currentImage].meta.status !== 'pending' ? (
        <VideoContainer>
          <VideoPlayer
            controls={!showPlayButton}
            playsInline
            ref={videoRef}
            poster={videoPoster}
            onEnded={() => {
              setShowPlayButton(true);
            }}
          >
            <source src={videoSrc} />
          </VideoPlayer>
          <MediaNavButtonLeft onClick={gotoPrevious} />
          <MediaNavButtonRight onClick={gotoNext} />
          {showPlayButton && (
            <VideoAction onClick={onVideoPlay} style={{ cursor: 'pointer' }}>
              <MediaPlayIcon
                customWrapperStyle={{ top: 0, position: 'relative', cursor: 'pointer', width: 'auto' }}
                onClick={onVideoPlay}
                maskId={`gallery-${currentImage}`}
              />
            </VideoAction>
          )}
        </VideoContainer>
      ) : (
        renderVideoProcessing()
      )}
      {showPrivatePhotoLabel && (
        <PrivatePhotoMessageContainer>
          <p
            style={{
              fontSize: 18,
              fontWeight: 600,
              margin: 0,
              padding: 0,
              color: '#E21D4A',
            }}
          >
            +{privatePhotoCount} <br />
            Private Photos/Videos
          </p>
          <p
            style={{
              fontSize: '12px',
              padding: '0 2rem',
              margin: '10px',
            }}
          >
            You can see private photos/videos if they respond to {privateText}
          </p>
        </PrivatePhotoMessageContainer>
      )}
      {(isOfferBtnVisible || showPrivatePhotoLabel) && (
        <div
          style={{
            position: 'fixed',
            zIndex: 1001,
            textAlign: 'center',
            bottom: '5%',
            left: 0,
            right: 0,
          }}
        >
          {ownProfile.isGenerous() ? renderControlProfileCompletion() : renderNewFeatureMandatoryPopup()}
        </div>
      )}
      {account && (
        <>
          <OfferSuccess
            isModalOpen={showOfferSuccessModal.isOpen}
            onClose={() => {
              setShowOfferSuccessModal({
                isOpen: false,
                amount: '',
                isCounter: false,
              });
            }}
            offerAmount={showOfferSuccessModal.amount}
            avatarSrc={avatar}
            username={account?.username}
            buttonID="interestedOffers"
            isCounter={showOfferSuccessModal.isCounter}
          />
          <FavOfferCreate
            isModalOpen={showFavOfferCreateModal}
            avatarSrc={avatar}
            username={account.username}
            hashId={account.hash_id}
            createNewOffer={handleCreateNewOffer}
            successOpen={handleOfferSuccess}
            counterOffer={handleCounterOffer}
            isCounter={false}
            currencyInitial={account?.currency?.data?.initial ?? ''}
            currencyLabel={account?.currency?.data?.label_unicode ?? ''}
            currentUserIsGenerous={ownProfile.isGenerous()}
            sex={account.profile.data.sex}
            userGender={account.profile.data.sex}
            sendSuggestion={handleSuggestion}
            cannot_make_offer_reasons={myprofile.cannot_make_offer_reasons}
            buttonID="interestedOffers"
            isSuggestRequest={false}
            pageTitle={'Profile'}
            otherProfile={account.profile}
            title={ownProfile.isGenerous() ? 'Make an Offer!' : 'Request A Date!'}
            closeModal={() => {
              setShowFavOfferCreateModal(false);
            }}
          />
        </>
      )}
      ;
    </React.Fragment>
  );
};

PhotoLightbox.propTypes = {
  lightboxIsOpen: PropTypes.bool.isRequired,
  privatePhotoCount: PropTypes.number,
  isOnboarded: PropTypes.bool,
  isBlocked: PropTypes.bool,
  onOfferClick: PropTypes.func.isRequired,
  justSuggested: PropTypes.bool,
  ignoreStatus: PropTypes.bool,
  username: PropTypes.string.isRequired,
  hashId: PropTypes.string.isRequired,
  isOfferBtnVisible: PropTypes.bool,
};

const mapStateToProps = (state: RootState) => {
  return {
    ownProfile: ProfileMaker.create(state.profile.profile),
    ownAccount: AccountMaker.create(state.profile),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch, ownProps: RootState) => {
  return {
    createNewOffer: (formData: any) => {
      return dispatch(createNewOffer(formData, true));
    },

    sendSuggestion: (username: string) => {
      return dispatch(sendSuggestion(username, false, null, ''));
    },
    counterOffer: (formData: any) => {
      return dispatch(counterOffer(formData, false, ownProps.navigate, null, null));
    },
    showWarn: (message: string) => {
      return dispatch(
        displayWarnMessage({
          info: message,
        })
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PhotoLightbox);
